import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SubLayout from '@/components/feature/SubLayout';
import { useSignedFileUrls } from '@/lib/storageSignedUrl';
import PhotoLightbox, { type LightboxPhoto } from '@/components/feature/PhotoLightbox';
import FileUploader from '@/components/feature/FileUploader';
import { createNotification } from '@/lib/notifications';
import { useSubcontractorAssignments } from '@/hooks/useSubcontractorAssignments';
import { useStageScheduleChecklist } from '@/hooks/useStageScheduleChecklist';
import { supabase } from '@/lib/supabase';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import { useTranslation } from 'react-i18next';

const tagColors: Record<string, string> = {
  Before: '#374151',
  Progress: '#E8621A',
  After: '#059669',
  Issue: '#EF4444',
};

const stageStatusStyle: Record<string, { bg: string; text: string; label: string }> = {
  completed: { bg: '#F0FDF4', text: '#059669', label: '' },
  in_progress: { bg: '#FEF0E7', text: '#E8621A', label: '' },
  not_started: { bg: '#F9FAFB', text: '#9CA3AF', label: '' },
  delayed: { bg: '#FEF2F2', text: '#EF4444', label: '' },
  on_hold: { bg: '#FFFBEB', text: '#F59E0B', label: '' },
  active: { bg: '#FEF0E7', text: '#E8621A', label: '' },
};

interface DBPhoto {
  id: string;
  file_url: string;
  file_name: string;
  note: string | null;
  created_at: string;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

interface RealStage {
  id: string;
  stage_name: string;
  description: string | null;
  status: string;
  completion_percent: number;
  planned_start_date: string | null;
  planned_end_date: string | null;
}

interface RealProject {
  id: string;
  project_name: string;
  project_code: string;
  site_address: string | null;
  status: string;
}

export default function SubcontractorStageDetail() {
  const { id, stageId } = useParams<{ id: string; stageId: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<RealProject | null>(null);
  const [stage, setStage] = useState<RealStage | null>(null);
  const [stages, setStages] = useState<RealStage[]>([]);
  const [dbPhotos, setDbPhotos] = useState<DBPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { t } = useTranslation();
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [updateTag, setUpdateTag] = useState<'Progress' | 'Issue' | 'Complete'>('Progress');
  const [currentUserId, setCurrentUserId] = useState<string>('');

  const stageLabels: Record<string, string> = {
    completed: t('sub.project.completedLabel'),
    in_progress: t('sub.project.inProgressLabel'),
    not_started: t('sub.project.upcomingLabel'),
    delayed: t('sub.project.delayedLabel'),
    on_hold: t('sub.project.onHoldLabel'),
    active: t('sub.project.inProgressLabel'),
  };

  const loadData = useCallback(async () => {
    if (!id || !stageId) return;
    setLoading(true);
    setError('');

    try {
      // Load project
      const { data: proj } = await supabase
        .from('jphph_projects')
        .select('id, project_name, project_code, site_address, status')
        .eq('id', id)
        .maybeSingle();

      if (import.meta.env.DEV) {
        console.log('[SubStageDetail] project query:', { id, found: !!proj });
      }

      if (!proj) {
        setError('Project not found');
        setLoading(false);
        return;
      }
      setProject(proj as RealProject);

      // Load stages
      const { data: stageRows } = await supabase
        .from('jphph_project_stages')
        .select('*')
        .eq('project_id', id)
        .order('sort_order', { ascending: true });

      const allStages = (stageRows || []) as RealStage[];
      setStages(allStages);

      const foundStage = allStages.find((s) => s.id === stageId);
      if (!foundStage) {
        setError('Stage not found');
        setLoading(false);
        return;
      }
      setStage(foundStage);

      // Load photos for this project (from project_files)
      const { data: photoRows, error: photoErr } = await supabase
        .from('jphph_project_files')
        .select('id, file_url, file_name, note, created_at, storage_bucket, storage_path')
        .eq('project_id', id)
        .eq('is_deleted', false)
        .in('file_type', ['jpg', 'jpeg', 'png', 'webp'])
        .order('created_at', { ascending: false });

      if (import.meta.env.DEV) {
        console.log('[SubStageDetail] photos query:', { id, count: photoRows?.length ?? 0, error: photoErr });
      }

      setDbPhotos((photoRows || []) as DBPhoto[]);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [id, stageId]);

  useEffect(() => {
    loadData();
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setCurrentUserId(data.user.id);
    });
  }, [loadData]);

  const { projectIds } = useSubcontractorAssignments();
  const { stages: calcStages, linkedMap, checklistItems } = useStageScheduleChecklist(id || '');

  // Filter checklist items for this stage that require sub check
  const stageChecklistItems = useMemo(() => {
    if (!stage || !id) return [];
    // Find schedule items that belong to this stage
    const stageScheduleItems = calcStages
      .find((s) => s.name === stage.stage_name)?.scheduleItems || [];
    const stageScheduleIds = new Set(stageScheduleItems.map((si) => si.id));

    // Find checklist items linked to those schedule items that require sub check
    const result: Array<{
      id: string;
      title: string;
      management_checked: boolean;
      subcontractor_checked: boolean;
      is_completed: boolean;
      scheduleItemTitle: string;
      scheduleItemId: string;
    }> = [];

    for (const [scheduleId, linked] of linkedMap.entries()) {
      if (!stageScheduleIds.has(scheduleId)) continue;
      const scheduleItem = stageScheduleItems.find((s) => s.id === scheduleId);
      for (const li of linked) {
        if (li.requires_subcontractor_check) {
          result.push({
            id: li.id,
            title: li.title,
            management_checked: li.management_checked,
            subcontractor_checked: li.subcontractor_checked,
            is_completed: li.is_completed,
            scheduleItemTitle: scheduleItem?.title || 'Unknown',
            scheduleItemId: scheduleId,
          });
        }
      }
    }
    return result;
  }, [calcStages, linkedMap, stage, id]);

  const pendingSubItems = stageChecklistItems.filter((i) => !i.subcontractor_checked);
  const completedSubItems = stageChecklistItems.filter((i) => i.subcontractor_checked);

  const handleSubCheck = useCallback(async (itemId: string) => {
    const now = new Date().toISOString();
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return;

    // Find the original checklist item to check requirements
    const originalItem = checklistItems.find((c) => c.id === itemId);
    if (!originalItem) return;

    const requiresMgmt = originalItem.requires_management_check !== false;
    const requiresSub = originalItem.requires_subcontractor_check === true;
    const nextSub = true;
    const nextCompleted = (!requiresMgmt || originalItem.management_checked) && (!requiresSub || nextSub);

    await supabase.from('jphph_checklist_items').update({
      subcontractor_checked: true,
      subcontractor_checked_at: now,
      subcontractor_checked_by: userId,
      is_completed: nextCompleted,
      completed_at: nextCompleted ? now : null,
      updated_at: now,
    }).eq('id', itemId);
  }, [checklistItems]);

  // Redirect if project id is not in assigned projects
  useEffect(() => {
    if (!id) {
      navigate('/subcontractor/dashboard', { replace: true });
      return;
    }
    if (projectIds.length > 0 && !projectIds.includes(id)) {
      if (import.meta.env.DEV) {
        console.warn('[SubStageDetail] Route param id not in assigned projects:', id, 'assigned:', projectIds);
      }
      navigate('/subcontractor/dashboard', { replace: true });
    }
  }, [id, projectIds, navigate]);

  const allPhotos = dbPhotos;
  const dbPhotoUrls = useSignedFileUrls(dbPhotos);
  const style = stageStatusStyle[stage?.status || 'not_started'];
  const stageLabel = stageLabels[stage.status] || stage.status;

  const lightboxItems: LightboxPhoto[] = allPhotos.map((p) => ({
    url: dbPhotoUrls[p.id] || p.file_url,
    caption: p.note || p.file_name,
    tag: t('sub.stage.progressTag'),
    tagColor: tagColors.Progress,
  }));

  const handleUploadComplete = async () => {
    setShowUploadPanel(false);
    setUploadSuccess(true);
    await loadData();
    await createNotification({
      project_id: id ?? null,
      notification_type: 'photo',
      title: t('sub.stage.photoUploadedSuccess'),
      body: 'A subcontractor uploaded new photos to the project.',
      target_role: 'management',
      link_path: `/management/projects/${id}`,
      created_by: currentUserId || undefined,
    });
    setTimeout(() => setUploadSuccess(false), 3000);
  };

  const handlePostUpdate = async () => {
    setShowUpdateForm(false);
    await createNotification({
      project_id: id ?? null,
      notification_type: 'progress',
      title: `${stage?.stage_name || 'Stage'} — Progress Report`,
      body: updateText || 'Work progressing well on site.',
      target_role: 'management',
      link_path: `/management/projects/${id}`,
      created_by: undefined,
    });
    setUpdateText('');
    setUploadSuccess(true);
    setTimeout(() => setUploadSuccess(false), 3000);
  };

  if (loading) {
    return (
      <SubLayout showBack backTo={`/subcontractor/project/${id}`}>
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-[#059669] text-3xl"></i>
        </div>
      </SubLayout>
    );
  }

  if (error || !project || !stage) {
    return (
      <SubLayout showBack backTo={`/subcontractor/project/${id}`}>
        <div className="text-center py-20 px-4">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-building-2-line text-5xl"></i>
          </div>
          <p className="text-[#374151] font-semibold mb-1">{error || t('sub.stage.stageNotFound')}</p>
          <button
            onClick={() => navigate(`/subcontractor/project/${id}`)}
            className="mt-3 text-[#E8621A] text-sm font-medium cursor-pointer"
          >
            {t('sub.stage.backToProject', { project: project?.project_name || '' })}
          </button>
        </div>
      </SubLayout>
    );
  }

  return (
    <SubLayout showBack backTo={`/subcontractor/project/${project.id}`}>
      <div className="w-full overflow-x-hidden pb-10">

        {/* Lightbox */}
        {lightboxIndex !== null && (
          <PhotoLightbox
            photos={lightboxItems}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}

        {/* Success toast */}
        {uploadSuccess && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-[#059669] text-white px-4 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2">
            <i className="ri-check-line"></i>
            {t('sub.stage.photoUploadedSuccess')}
          </div>
        )}

        {/* Header */}
        <div className="px-4 lg:px-8 pt-5">
          <button
            onClick={() => navigate(`/subcontractor/project/${project.id}`)}
            className="flex items-center gap-1.5 text-[#9CA3AF] text-sm mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-base"></i>
            {t('sub.stage.backToProject', { project: project.project_name })}
          </button>

          {/* Project context pill */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1.5 bg-[#F0FDF4] px-2.5 py-1 rounded-full">
              <i className="ri-building-2-line text-[10px] text-[#059669]"></i>
              <span className="text-[10px] font-bold text-[#059669]">{project.project_name}</span>
            </div>
            <i className="ri-arrow-right-s-line text-[#D1D5DB] text-sm"></i>
            <div className="flex items-center gap-1.5 bg-[#FEF0E7] px-2.5 py-1 rounded-full">
              <i className="ri-tools-line text-[10px] text-[#E8621A]"></i>
              <span className="text-[10px] font-bold text-[#E8621A]">{stage.stage_name}</span>
            </div>
          </div>

          {/* Stage header card */}
          <div className="bg-[#1A1A1A] rounded-2xl p-5 mb-5 relative overflow-hidden">
            <div className="absolute right-0 top-0 bottom-0 w-32 opacity-10">
              <div className="w-full h-full bg-[#E8621A] rounded-l-full"></div>
            </div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${style.text}20`, color: style.text }}
                >
                  {stageLabel}
                </span>
                <span className="text-[#6B7280] text-xs">
                  {stage.planned_start_date
                    ? `${new Date(stage.planned_start_date).toLocaleDateString(t('date.locale') || 'en-NZ', { day: 'numeric', month: 'short' })} — ${stage.planned_end_date ? new Date(stage.planned_end_date).toLocaleDateString(t('date.locale') || 'en-NZ', { day: 'numeric', month: 'short' }) : t('sub.project.tbc')}`
                    : t('sub.common.noDatesSet')
                  }
                </span>
              </div>
              <h1 className="text-white text-xl font-bold mb-1">{stage.stage_name}</h1>
              <p className="text-[#9CA3AF] text-sm leading-relaxed">{stage.description || ''}</p>

              {(stage.status === 'in_progress' || stage.status === 'active') && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[#9CA3AF] text-xs">{t('sub.stage.stageProgress')}</span>
                    <span className="text-[#E8621A] text-sm font-bold">{stage.completion_percent}%</span>
                  </div>
                  <div className="h-2 bg-[#374151] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#E8621A] transition-all"
                      style={{ width: `${stage.completion_percent}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1.5">
                  <i className="ri-image-line text-[#9CA3AF] text-sm"></i>
                  <span className="text-[#9CA3AF] text-xs">{allPhotos.length} {t('sub.dailyLog.noPhotos')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Photo section */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-[#1A1A1A] text-sm">{t('sub.stage.stagePhotos')}</h2>
              <button
                onClick={() => setShowUploadPanel(true)}
                className="flex items-center gap-1.5 bg-[#059669] text-white px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-upload-2-line"></i>
                {t('sub.stage.uploadPhoto')}
              </button>
            </div>

            {allPhotos.length === 0 ? (
              <button
                onClick={() => setShowUploadPanel(true)}
                className="w-full border-2 border-dashed border-gray-200 rounded-2xl py-10 flex flex-col items-center gap-2 cursor-pointer hover:border-[#059669] transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <i className="ri-camera-line text-2xl text-[#9CA3AF]"></i>
                </div>
                <span className="text-[#9CA3AF] text-sm">{t('sub.stage.noPhotos')}</span>
              </button>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {allPhotos.map((photo, i) => {
                  return (
                    <div
                      key={photo.id}
                      className="w-full aspect-[4/3] overflow-hidden rounded-xl bg-slate-50"
                    >
                      <button
                        onClick={() => setLightboxIndex(i)}
                        className="relative w-full h-full cursor-pointer group text-left"
                      >
                        <ProjectPhotoImage
                          src={photo.file_url}
                          alt={photo.file_name || 'Stage photo'}
                          sizes="(max-width: 768px) 50vw, 25vw"
                          loading="lazy"
                          debugContext="SubcontractorStage"
                        />
                        <div className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: tagColors.Progress }}>
                          {t('sub.stage.progressTag')}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                            <i className="ri-zoom-in-line text-white text-lg"></i>
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                          <p className="text-white text-xs font-medium truncate">{photo.note || photo.file_name}</p>
                        </div>
                      </button>
                    </div>
                  );
                })}
                {/* Add more button */}
                <button
                  onClick={() => setShowUploadPanel(true)}
                  className="relative rounded-xl overflow-hidden aspect-[4/3] border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#059669] transition-colors"
                >
                  <i className="ri-add-line text-2xl text-[#9CA3AF]"></i>
                  <span className="text-[10px] text-[#9CA3AF]">{t('sub.stage.addPhoto')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Upload panel */}
          {showUploadPanel && currentUserId && (
            <FileUploader
              projectId={id || ''}
              currentUserId={currentUserId}
              open={showUploadPanel}
              onClose={() => setShowUploadPanel(false)}
              onUploadComplete={handleUploadComplete}
              accountType="subcontractor"
              category="photo"
              allowMultiple={true}
              allowCamera={true}
              enablePerFileCaption={true}
              title={`${t('sub.stage.uploadPhotos')} ${stage.stage_name}`}
            />
          )}

          {/* Post Update section */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-[#1A1A1A] text-sm">{t('sub.stage.stageUpdates')}</h2>
              <button
                onClick={() => setShowUpdateForm(true)}
                className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                {t('sub.stage.postUpdate')}
              </button>
            </div>

            {/* Post update form */}
            {showUpdateForm && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-[#1A1A1A] text-sm">{t('sub.stage.postUpdateTitle')}</h4>
                  <button
                    onClick={() => { setShowUpdateForm(false); setUpdateText(''); }}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-[#6B7280] cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                  {(['Progress', 'Issue', 'Complete'] as const).map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setUpdateTag(tag)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                        updateTag === tag ? 'bg-[#E8621A] text-white' : 'bg-gray-100 text-[#6B7280]'
                      }`}
                    >
                      {tag === 'Progress' ? t('sub.stage.progressTag') : tag === 'Issue' ? t('sub.stage.issueTag') : t('sub.stage.completeTag')}
                    </button>
                  ))}
                </div>
                <textarea
                  value={updateText}
                  onChange={(e) => setUpdateText(e.target.value)}
                  placeholder={t('sub.stage.describeUpdate')}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
                />
                <div className="flex items-center justify-between mt-1 mb-3">
                  <span className="text-[#D1D5DB] text-xs">{updateText.length}/500</span>
                </div>
                <button
                  onClick={handlePostUpdate}
                  disabled={!updateText.trim()}
                  className="w-full py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {t('sub.stage.postUpdate')}
                </button>
              </div>
            )}

            {/* Empty state for updates */}
            <div className="text-center py-6">
              <p className="text-[#9CA3AF] text-xs">{t('sub.stage.updatesWillAppear')}</p>
            </div>
          </div>

          {/* Subcontractor Checklist Section */}
          {stageChecklistItems.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-[#1A1A1A] text-sm">{t('sub.stage.myChecklistItems')}</h2>
                <span className="text-[10px] font-bold text-[#0891B2] bg-[#ECFEFF] px-2 py-0.5 rounded-full">
                  {t('sub.stage.doneCount', { completed: completedSubItems.length, total: stageChecklistItems.length })}
                </span>
              </div>

              {/* Pending items */}
              {pendingSubItems.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-[10px] font-semibold text-[#92400E] uppercase tracking-wider px-1">{t('sub.stage.pending')} ({pendingSubItems.length})</p>
                  {pendingSubItems.map((item) => (
                    <div key={item.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-0.5 flex-shrink-0 mt-0.5">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 bg-white border-gray-200">
                            <span className="text-[8px] font-bold text-[#9CA3AF]">S</span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#374151] leading-snug">{item.title}</p>
                          <p className="text-[10px] text-[#9CA3AF] mt-0.5">{item.scheduleItemTitle}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
                              {item.management_checked ? t('sub.stage.mgmtDone') : t('sub.stage.mgmtPending')}
                            </span>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FFFBEB] text-[#F59E0B]">
                              {t('sub.stage.subPending')}
                            </span>
                            {item.management_checked && (
                              <span className="text-[9px] font-bold bg-[#FFFBEB] text-[#F59E0B] px-1.5 py-0.5 rounded-full">
                                {t('sub.stage.waitingForSubcontractor')}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleSubCheck(item.id)}
                          className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-[#0891B2] text-white text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#0E7490] transition-colors"
                        >
                          <i className="ri-check-line mr-1"></i>{t('sub.stage.checkLabel')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Completed items */}
              {completedSubItems.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-semibold text-[#059669] uppercase tracking-wider px-1">{t('sub.stage.completed')} ({completedSubItems.length})</p>
                  {completedSubItems.map((item) => (
                    <div key={item.id} className="bg-white rounded-2xl border border-[#059669]/20 p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-0.5 flex-shrink-0 mt-0.5">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 bg-[#0891B2] border-[#0891B2]">
                            <i className="ri-check-line text-white text-[8px]"></i>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#9CA3AF] leading-snug line-through">{item.title}</p>
                          <p className="text-[10px] text-[#9CA3AF] mt-0.5">{item.scheduleItemTitle}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669]">
                              {t('sub.stage.mgmtDone')}
                            </span>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#ECFEFF] text-[#0891B2]">
                              {t('sub.stage.subDone')}
                            </span>
                            <span className="text-[9px] font-bold bg-[#F0FDF4] text-[#059669] px-1.5 py-0.5 rounded-full">
                              {t('sub.stage.finalComplete')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{t('sub.stage.subcontractorCheckProgress')}</span>
                  <span className="text-xs font-bold text-[#0891B2]">
                    {stageChecklistItems.length > 0 ? Math.round((completedSubItems.length / stageChecklistItems.length) * 100) : 0}%
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#0891B2] transition-all duration-500"
                    style={{ width: `${stageChecklistItems.length > 0 ? (completedSubItems.length / stageChecklistItems.length) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </SubLayout>
  );
}